import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AutoComplete,
  type AutoCompleteChangeEvent,
  type AutoCompleteCompleteEvent,
  type AutoCompleteSelectEvent,
} from 'primereact/autocomplete'
import { Column } from 'primereact/column'
import DataTable from '../../../components/ui/DataTable'
import { useToast } from '../../../components/ui/ToastProvider'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../../lib/useMediaQuery'
import { useEmployeeSearch } from '../hooks/useEmployeeSearch'
import ManagerListPicker from '../../managerLists/components/ManagerListPicker'
import type { ManagerList } from '../../managerLists/types'
import { sortParticipants } from '../participantOrder'
import type { EmployeePick } from '../types'
import '../../meetings/meetings.css'

interface Props {
  participants: EmployeePick[]
  onChange: (participants: EmployeePick[]) => void
  error?: string | null
  /**
   * Metin anahtarlarının kökü (119). Varsayılan katılımcılar; dağıtım listesi aynı bileşeni
   * `minutes.distributionList` ile kullanır — anahtar kümesi iki kökte de aynıdır.
   */
  i18nPrefix?: string
  /** Başlıkta zorunluluk işareti (katılımcılar zorunlu, dağıtım listesi değil). */
  required?: boolean
  /** Arama kutusunun id'si — aynı sayfada iki seçici olduğunda çakışmasın diye (119). */
  inputId?: string
  /**
   * Bu listeye EKLENEMEYECEK siciller — diğer listedekiler (119: bir kişi hem katılımcı hem
   * dağıtım listesinde olamaz). Öneri listesinde görünmezler ve isim listesiyle eklemede atlanırlar;
   * sunucu da aynı kuralı ayrıca zorlar.
   */
  excludeIds?: readonly string[]
}

/** Arama kutusunun varsayılan id'si — hata kutusunun `id`'si bundan türetilir (`FormField` ile aynı sözleşme). */
const DEFAULT_INPUT_ID = 'minutes-participant-search'
const NO_EXCLUDES: readonly string[] = []

/**
 * Katılımcılar bölümü (039, US1). PrimeReact {@link AutoComplete} <b>yalnız arama kutusu</b> olarak kullanılır
 * (seçilenler token olarak burada gösterilmez); seçim yapılınca kişi aşağıdaki tabloya eklenir ve giriş temizlenir.
 * SAP HR araması (aktif + müdür ve üstü); sicil bazında mükerrerlik engellenir (FR-010). Çıkarma <b>tablo</b>
 * üzerinden yapılır. En az bir katılımcı zorunlu (üstte hata gösterilir).
 */
export default function ParticipantsSection({
  participants,
  onChange,
  error,
  i18nPrefix = 'minutes.participants',
  required = true,
  inputId = DEFAULT_INPUT_ID,
  excludeIds = NO_EXCLUDES,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const toast = useToast()
  const { suggestions: rawSuggestions, search } = useEmployeeSearch()
  const errId = `${inputId}-err`
  const excluded = new Set(excludeIds)
  const suggestions = rawSuggestions.filter((s) => !excluded.has(s.employeeId))
  const acRef = useRef<AutoComplete>(null)
  // Dar ekranda (≤CARD_VIEW_BREAKPOINT) tablo yerine kart listesi — mtg-participant-cards (meetings.css).
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)
  // Arama kutusunun ham metni (seçili kişileri tutmaz — onlar tabloda). Seçimden sonra temizlenir.
  const [query, setQuery] = useState('')

  const complete = (e: AutoCompleteCompleteEvent) => search(e.query)

  // Odaklanınca (dışarı tıklayıp geri dönünce): girişte ≥2 karakter varsa paneli yeniden aç. `onInputFocus`
  // tek başına paneli açmadığından PrimeReact'in kendi search'ünü imperatif tetikleriz (searchingState=true).
  const reopenOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const q = e.target.value ?? ''
    if (q.trim().length >= 2) acRef.current?.search(e, q, null)
  }

  // Yazarken: string ise metni tut; nesne (klavye ile vurgulanan) ise ad-soyadı göster (seçim onSelect'te).
  const onQueryChange = (e: AutoCompleteChangeEvent) => {
    const v = e.value
    setQuery(typeof v === 'string' ? v : ((v as EmployeePick)?.fullName ?? ''))
  }

  // Bir öneri seçilince: sicil bazında tekil ekle, girişi temizle (kutu boş kalsın → sonraki arama).
  const onSelect = (e: AutoCompleteSelectEvent) => {
    const picked = e.value as EmployeePick
    if (
      picked?.employeeId &&
      !excluded.has(picked.employeeId) &&
      !participants.some((p) => p.employeeId === picked.employeeId)
    ) {
      // Yeni kişi listenin SONUNA değil, DOĞRU SIRASINA girer (kullanıcı isteği 30.09.2026): liste
      // her eklemede yeniden dizilir. Aksi halde ekranda ekleme sırası görünür, kaydettikten sonra
      // bambaşka bir sıra çıkardı — kural sunucudakiyle aynıdır (bkz. `participantOrder`).
      onChange(sortParticipants([...participants, picked]))
    }
    setQuery('')
  }

  /**
   * İsim listesiyle toplu ekleme (115): listenin kişileri katılımcılara katılır ve her biri listenin
   * **etiketini** taşır. Gösterim değişMEZ — katılımcılar tek tek görünmeye devam eder (kullanıcı
   * isteği 01.10.2026); etiket yalnız kaydedilir.
   *
   * <p>Zaten ekli sicil **atlanır ve etiketi değişmez**: tek tek eklenmiş bir kişi, liste eklendi
   * diye o listeden gelmiş sayılmamalı. Küme her eklemede **yeniden sıralanır** (unvan grubu + ad) —
   * tek kişi eklemedeki kuralın aynısı, aksi halde listeyle gelenler sona yığılırdı.
   */
  const addList = (ml: ManagerList) => {
    const existing = new Set([...participants.map((p) => p.employeeId), ...excludeIds])
    const added: EmployeePick[] = ml.people
      .filter((p) => !existing.has(p.employeeId))
      .map((p) => ({
        employeeId: p.employeeId,
        fullName: p.fullName || p.employeeId,
        jobTitle: p.jobTitleTr,
        businessMail: p.businessMail,
        firstName: p.firstName,
        lastName: p.lastName,
        jobTitleTr: p.jobTitleTr,
        jobTitleEn: p.jobTitleEn,
        // Unvan grubu kodu listede saklanMIYOR: kaydedilen değeri sunucu zaten HR'dan çözüyor.
        // Kayıt öncesi sıralamada bu kişiler (kodsuz) sona düşer; kaydedildikten sonra doğru yere
        // girer. Bilinçli: kodu burada uydurmak, sunucunun çözeceğinden farklı olabilirdi.
        employeeRoleGroupCode: null,
        managerListId: ml.id,
      }))
    if (added.length > 0) onChange(sortParticipants([...participants, ...added]))
    // Diğer listede olduğu için atlanan kişiler söylenir (119): aksi hâlde listenin tamamı diğer
    // listedeyken seçim hiçbir şey yapmıyor ve bozuk görünüyordu (kullanıcı bildirimi 07.10.2026).
    const skipped = ml.people.filter(
      (p) => excluded.has(p.employeeId) && !participants.some((x) => x.employeeId === p.employeeId),
    ).length
    if (skipped > 0) toast.warn(t(`${i18nPrefix}.skippedFromList`, { count: skipped }))
  }

  /** Katılımcı çıkar — kalanların liste bağı korunur (bkz. prensip kararı dialogundaki ikiz kural). */
  const removeParticipant = (sicil: string) => {
    onChange(participants.filter((p) => p.employeeId !== sicil))
  }

  return (
    <div className="minutes-subsection">
      <h3 className="minutes-subhead">
        {t(`${i18nPrefix}.title`)} {required && <span className="ui-req">*</span>}
      </h3>
      {/* Kişi araması ve liste seçici YAN YANA (kullanıcı isteği 01.10.2026): ikisi de "katılımcı
          ekleme" eylemidir, ayrı satırlarda durunca liste kutusu ilgisiz bir alan gibi görünüyordu.
          Dar ekranda alt alta düşerler (bkz. minutes.css `.minutes-add-row`). */}
      <div className="minutes-add-row">
        <AutoComplete
          ref={acRef}
        value={query}
        suggestions={suggestions}
        completeMethod={complete}
        field="fullName"
        delay={300}
        minLength={2}
        placeholder={t(`${i18nPrefix}.placeholder`)}
        onChange={onQueryChange}
        onSelect={onSelect}
        onFocus={reopenOnFocus}
        panelClassName="minutes-participant-panel"
        itemTemplate={(item: EmployeePick) => (
          <div className="minutes-emp-item">
            <span className="minutes-emp-name">{item.fullName}</span>
            {item.jobTitle && <span className="minutes-emp-title">{item.jobTitle}</span>}
            <span className="minutes-emp-sicil">{item.employeeId}</span>
          </div>
        )}
        className={error ? 'p-invalid minutes-participant-input' : 'minutes-participant-input'}
        inputId={inputId}
        /* ARIA nitelikleri bileşenin köküne değil içteki `<input>`'a yazılmalı; passthrough
           (`pt`) bunun için var. TEKİL modda hedef `input.root`'tur — girdi, iç içe bir
           `InputText`'in kökü olarak çizilir (`data-pc-section="root"`, ölçüldü 25.09.2026).
           ÇOKLU modda biçim farklıdır; bkz. `UserMultiPicker`. */
        pt={{
          input: {
            root: {
              'aria-invalid': error ? 'true' : 'false',
              'aria-describedby': errId,
            },
          },
        }}
        />
        <ManagerListPicker
          onPick={addList}
          placeholder={t(`${i18nPrefix}.listPlaceholder`)}
        />
      </div>
      {/* Hata kutusu HER ZAMAN DOM'da, yalnız içeriği doluyor — koşullu EKLENEN bir canlı
          bölgeyi bazı ekran okuyucular duyurmaz (THY inceleme bulgusu, 16.09.2026; aynı kusur
          25.09'da Notlarım paylaşımında da çıktı). Eskiden yalnız `p-invalid` sınıfı vardı:
          görsel bir işaret, sesli karşılığı YOK.

          Paylaşılan `FormField` burada TÜKETİLEMİYOR: bölüm başlığı bir `<h3>`, `FormField`
          ise kendi `<label>`'ını çiziyor — kullanmak ekrana ikinci bir görünür etiket
          eklerdi. Mekanizma yine de onunla aynı: `.ui-field-err` sınıfı (boşken
          `.ui-field-err:empty` ile hiç yer kaplamaz), `polite` duyuru ve `${id}-err`
          sözleşmesi. Yeni CSS yazılmadı. */}
      <div className="ui-field-err" id={errId} aria-live="polite">
        {error}
      </div>

      {participants.length > 0 &&
        (isMobile ? (
          <div className="ui-card-list mtg-participant-cards">
            {participants.map((p) => (
              <div key={p.employeeId} className="ui-card">
                <div className="ui-card-head">
                  <span />
                  <div className="ui-card-actions">
                    <button
                      type="button"
                      className="minutes-remove-btn"
                      aria-label={t(`${i18nPrefix}.remove`)}
                      title={t(`${i18nPrefix}.remove`)}
                      onClick={() => removeParticipant(p.employeeId)}
                    >
                      <i className="pi pi-trash" aria-hidden="true" />
                    </button>
                  </div>
                </div>
                <span className="ui-card-title">{p.fullName}</span>
                <span className="ui-card-sub">{p.employeeId}</span>
                <div className="ui-card-row">{p.jobTitleTr ?? p.jobTitle ?? ''}</div>
              </div>
            ))}
          </div>
        ) : (
          <DataTable
            value={participants}
            dataKey="employeeId"
            paginator={false}
            className="minutes-participant-table"
          >
            <Column
              header={t(`${i18nPrefix}.columns.name`)}
              body={(p: EmployeePick) => <span className="minutes-cell-strong">{p.fullName}</span>}
            />
            <Column header={t(`${i18nPrefix}.columns.sicil`)} field="employeeId" />
            <Column
              header={t(`${i18nPrefix}.columns.title`)}
              body={(p: EmployeePick) => p.jobTitleTr ?? p.jobTitle ?? ''}
            />
            <Column
              header=""
              headerStyle={{ width: '3rem' }}
              bodyStyle={{ textAlign: 'center' }}
              body={(p: EmployeePick) => (
                // type="button": form içinde submit tetiklemesin; stopPropagation ile satır/olay sızması olmasın.
                <button
                  type="button"
                  className="minutes-remove-btn"
                  aria-label={t(`${i18nPrefix}.remove`)}
                  title={t(`${i18nPrefix}.remove`)}
                  onClick={(ev) => {
                    ev.preventDefault()
                    ev.stopPropagation()
                    removeParticipant(p.employeeId)
                  }}
                >
                  <i className="pi pi-trash" aria-hidden="true" />
                </button>
              )}
            />
          </DataTable>
        ))}
    </div>
  )
}
